import { useState } from 'react'
import GoalCard from '../components/GoalCard.jsx'
import SubGoalTable from '../components/SubGoalTable.jsx'
import AddGoalButton from '../components/AddGoalButton.jsx'
import EditableValue from '../components/EditableValue.jsx'
import { usePillarData } from '../queries/usePillarData.js'
import { useGoalsCrud } from '../mutations/useGoalsCrud.js'
import { PILLARS } from '../data/pillars.js'

/**
 * BasicPillar — renders a standard pillar: header, list of GoalCards
 * (each with optional SubGoalTable), and an "+ Add goal" button.
 *
 * All the per-goal infrastructure (weekly summary, action items,
 * comments, attribution, presence chips, HubSpot readonly) lives inside
 * GoalCard, so every pillar inherits it for free.
 *
 * Props:
 *   pillarId     — id from PILLARS (e.g. 'growth')
 *   description  — short italic line under the header (optional)
 *
 * This is the "no-special-cases" pillar. Pillars that need custom data
 * sources or unusual chrome (e.g. Revenue pulling HubSpot metrics) can
 * skip this and render their own shell.
 */
export default function BasicPillar({ pillarId, description }) {
  const meta = PILLARS.find((p) => p.id === pillarId)
  const pillarName = meta?.name || pillarId
  const { pillar, goals, isLoading, isFetching, error } = usePillarData(pillarName)

  if (isLoading) {
    return (
      <div className="placeholder-card">
        <p>Loading {pillarName}…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="placeholder-card"
        style={{ borderColor: '#fecaca', background: '#fef2f2', color: '#991b1b' }}
      >
        <p>Failed to load {pillarName} data: {error.message}</p>
      </div>
    )
  }

  if (!pillar) {
    return (
      <div className="placeholder-card">
        <p>No {pillarName} pillar data found in the shared store.</p>
        <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
          Open <code>/q2-goals.html</code> once to seed the KV store, then refresh.
        </p>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 4,
          }}
        >
          <span style={{ fontSize: 20 }}>{meta?.icon || '•'}</span>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: meta?.color || 'var(--text)' }}>
            {meta?.name || pillarName}
          </h2>
          {isFetching && (
            <span style={{ fontSize: 10, color: 'var(--muted)', fontStyle: 'italic' }}>
              refreshing…
            </span>
          )}
        </div>
        <PillarDescription
          pillarName={pillarName}
          value={pillar.description ?? description}
        />
      </div>

      {/* Goals list */}
      {goals.length === 0 ? (
        <div
          style={{
            fontSize: 12,
            color: 'var(--muted-light)',
            fontStyle: 'italic',
            padding: '20px 0',
            textAlign: 'center',
          }}
        >
          No goals yet. Add one below to get started.
        </div>
      ) : (
        (() => {
          // Find index of the first non-section goal so the tour has a
          // stable anchor for the "first goal" steps.
          const firstRealIdx = goals.findIndex((g) => g?.type !== 'section')
          return goals.map((goal, gi) => {
            if (goal.type === 'section') {
              return (
                <SectionDivider
                  key={gi}
                  pillarName={pillarName}
                  goalIndex={gi}
                  label={goal.goal}
                  color={meta?.color || 'var(--muted)'}
                />
              )
            }
            const hasSubgoals = Array.isArray(goal.subgoals) && goal.subgoals.length > 0
            return (
              <GoalCard
                key={gi}
                goal={goal}
                pillarColor={meta?.color || 'var(--muted)'}
                pillarName={pillarName}
                goalIndex={gi}
                isFirst={gi === firstRealIdx}
              >
                {hasSubgoals && (
                  <SubGoalTable
                    pillarName={pillarName}
                    goalIndex={gi}
                    goal={goal}
                    subgoals={goal.subgoals}
                  />
                )}
              </GoalCard>
            )
          })
        })()
      )}

      {/* Add goal */}
      <AddGoalButton pillarName={pillarName} pillarColor={meta?.color || 'var(--muted)'} />
    </div>
  )
}

/**
 * PillarDescription — click-to-edit description line under the pillar
 * header. The first saved value writes a `description` field onto the
 * pillar object in KV; every subsequent render prefers that value over
 * the hardcoded fallback from each per-pillar component.
 */
function PillarDescription({ pillarName, value }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value || '')
  const { updatePillar } = useGoalsCrud()

  const save = () => {
    const trimmed = (draft || '').trim()
    if (trimmed !== (value || '').trim()) {
      updatePillar({ pillarName, field: 'description', value: trimmed })
    }
    setEditing(false)
  }

  if (editing) {
    return (
      <textarea
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setDraft(value || '')
            setEditing(false)
          }
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            save()
          }
        }}
        placeholder="Describe this pillar…"
        style={{
          width: '100%',
          minHeight: 40,
          padding: '6px 8px',
          border: '1px solid var(--accent)',
          borderRadius: 6,
          fontSize: 12,
          fontFamily: 'inherit',
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
          resize: 'vertical',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    )
  }

  return (
    <p
      onClick={() => {
        setDraft(value || '')
        setEditing(true)
      }}
      title="Click to edit"
      style={{
        fontSize: 12,
        color: value ? 'var(--muted)' : 'var(--muted-light)',
        fontStyle: value ? 'normal' : 'italic',
        cursor: 'text',
        margin: 0,
        padding: '2px 0',
      }}
    >
      {value || 'Click to add a description…'}
    </p>
  )
}

/**
 * SectionDivider — some pillars (Growth, Delivery, Team) use
 * `{ type: 'section', goal: 'Label…' }` entries to group related goals
 * under a themed heading.
 *
 * Like goals and sub-goals, the label is click-to-edit via EditableValue
 * (which handles the optimistic mutation). A delete × appears on hover.
 *
 * Props are optional so standalone uses (outside a pillar, e.g. stories)
 * still render a static divider.
 */
export function SectionDivider({ label, color, pillarName, goalIndex }) {
  const [hovered, setHovered] = useState(false)
  const { removeGoal } = useGoalsCrud()
  const canMutate = pillarName != null && goalIndex != null

  const handleDelete = () => {
    if (confirm(`Delete the section "${label}"?`)) {
      removeGoal({ pillarName, goalIndex })
    }
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '18px 0 8px',
        paddingBottom: 6,
        borderBottom: `1.5px solid ${color}20`,
      }}
    >
      <div
        style={{
          height: 2,
          width: 18,
          borderRadius: 2,
          background: color,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color,
          fontStyle: 'italic',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          flex: 1,
          minWidth: 0,
        }}
      >
        {canMutate ? (
          <EditableValue
            pillarName={pillarName}
            goalIndex={goalIndex}
            field="goal"
            value={label}
            inputType="text"
          />
        ) : (
          label
        )}
      </span>
      {canMutate && (
        <button
          type="button"
          onClick={handleDelete}
          title={`Delete section "${label}"`}
          aria-label="Delete section"
          style={{
            width: 20,
            height: 20,
            border: 'none',
            background: 'transparent',
            color: 'var(--muted-light)',
            fontSize: 12,
            cursor: 'pointer',
            opacity: hovered ? 0.7 : 0,
            transition: 'opacity 0.15s, color 0.15s, background 0.15s',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1'
            e.currentTarget.style.color = '#dc2626'
            e.currentTarget.style.background = '#fee2e2'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = hovered ? '0.7' : '0'
            e.currentTarget.style.color = 'var(--muted-light)'
            e.currentTarget.style.background = 'transparent'
          }}
        >
          ✕
        </button>
      )}
    </div>
  )
}
